import { useEffect, useState, useCallback } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import { Link } from 'foundations-components/transitional/components/Link';
import { ScrollContainer } from 'foundations-components/transitional/components/ScrollContainer';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import CompanionFooter from './CompanionFooter';
import InteractiveContentLoading from './InteractiveContentLoading';
import { MOCK_QUIZ_ME_DATA } from '../mockData';
import { useVideoTimestamp } from '../../video/videoHooks';
import { useCurrentVideoTime } from '../../video/components/VideoPlayerWithControls';
import { useShortVideo } from '../../shortContent/shortContentHooks';
import UsageTracker from '../../../utils/UsageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
// Format seconds to hh:mm:ss (or mm:ss when hours are 0)
const formatSecondsToTimestamp = totalSeconds => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor(totalSeconds % 3600 / 60);
  const s = Math.floor(totalSeconds % 60);
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
};
const SummarizeTab = ({
  additionalTracking,
  contentId,
  contentType
}) => {
  var _useShortVideo, _contentId, _summary$sections;
  const [loading, setLoading] = useState(true);

  // Video player controls and state
  const {
    setVideoTimestamp
  } = useVideoTimestamp();
  const {
    currentVideoTime,
    hasVideoPlayed,
    isVideoFinished
  } = useCurrentVideoTime();
  const videoDuration = (_useShortVideo = useShortVideo({
    shortId: contentId
  })) === null || _useShortVideo === void 0 ? void 0 : _useShortVideo.videoDuration;

  // TODO: Replace with actual API call once backend is implemented
  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(t);
  }, [contentId]);

  // TODO: Replace with actual API data once backend is implemented
  const summary = MOCK_QUIZ_ME_DATA === null || MOCK_QUIZ_ME_DATA === void 0 || (_contentId = MOCK_QUIZ_ME_DATA[contentId]) === null || _contentId === void 0 ? void 0 : _contentId.summary;

  /**
   * Handles chapter navigation clicks and tracks analytics
   * Captures accurate video state based on actual play/finish events
   */
  const handleChapterClick = useCallback((chapterIndex, chapterTitle, chapterTimestamp) => {
    // Calculate video progress as a percentage (0-1)
    const videoProgress = videoDuration && videoDuration > 0 && currentVideoTime >= 0 ? Math.min(currentVideoTime / videoDuration, 1) : 0;

    // Determine video playback state based on actual user interaction
    let videoPlaybackState;
    if (isVideoFinished) videoPlaybackState = 'finished';else if (hasVideoPlayed) videoPlaybackState = 'started';else videoPlaybackState = 'not_started';
    const trackingData = Object.assign({
      action: 'ai-summarization-chapter-clicked',
      position: chapterIndex,
      // Start from 0
      title: chapterTitle,
      timestamp: chapterTimestamp,
      // Video timestamp in seconds where chapter starts
      progressLevel: videoProgress,
      // Current video progress as percentage (0-1)
      state: videoPlaybackState
    }, additionalTracking);
    UsageTracker.trackStandalone('Learning Center Interaction', trackingData);

    // Navigate video to the selected chapter timestamp
    setVideoTimestamp(chapterTimestamp);
  }, [currentVideoTime, additionalTracking, setVideoTimestamp, videoDuration, hasVideoPlayed, isVideoFinished]);

  // TODO: Uncomment and replace with actual regenerate API call once backend is implemented
  // const handleRegenerate = () => {}

  if (loading) {
    return /*#__PURE__*/_jsx(View, {
      paddingBlockStart: "700",
      paddingInline: "700",
      children: /*#__PURE__*/_jsx(InteractiveContentLoading, {})
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(ScrollContainer, {
      blockSize: "450px",
      maxHeight: 450,
      paddingBlockStart: "700",
      paddingInline: "700",
      children: !summary ? /*#__PURE__*/_jsx(View, {
        marginBlockStart: "800",
        children: /*#__PURE__*/_jsx(Text, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.interactiveEducation.summarize.summaryError"
          })
        })
      }) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [summary.description && /*#__PURE__*/_jsx(View, {
          marginBlockEnd: "800",
          children: /*#__PURE__*/_jsx(Text, {
            children: summary.description
          })
        }), (_summary$sections = summary.sections) === null || _summary$sections === void 0 ? void 0 : _summary$sections.map(({
          title,
          bullets,
          timestamp
        }, index) => {
          return /*#__PURE__*/_jsxs(View, {
            marginBlockEnd: "600",
            children: [/*#__PURE__*/_jsx(View, {
              alignItems: "baseline",
              gap: "300",
              children: /*#__PURE__*/_jsxs(Link, {
                onClick: () => handleChapterClick(index, title, timestamp),
                children: [formatSecondsToTimestamp(timestamp), " - ", title]
              })
            }), /*#__PURE__*/_jsx(List, {
              marginBlockStart: "200",
              paddingInlineStart: "800",
              styled: true,
              children: bullets === null || bullets === void 0 ? void 0 : bullets.map((bullet, i) => /*#__PURE__*/_jsx(ListItem, {
                children: /*#__PURE__*/_jsx(Text, {
                  children: bullet
                })
              }, i))
            })]
          }, timestamp);
        })]
      })
    }), /*#__PURE__*/_jsx(CompanionFooter, {
      additionalButtons:
      // TODO: Uncomment regenerate button once backend API is implemented
      // <Button onClick={handleRegenerate} use="primary">
      //   <FormattedMessage message="learningCenter.interactiveEducation.summarize.regenerateSummary" />
      // </Button>
      undefined,
      contentId: contentId,
      contentType: contentType,
      trackingAction: "AI_SUMMARIZE_FEEDBACK"
    })]
  });
};
export default SummarizeTab;